import secureEssentialsIcon from '../_assets/icons/secure-essentials.svg'
import securePremiumIcon from '../_assets/icons/secure.svg'

/** Shared z-index values for overlays and modals. Keep in sync with SASS when needed (e.g. $modal-zindex in _config.scss). */
export const Z_INDEX = {
  MODAL: 799999999,
  ABOVE_MODAL: 800000000,
}

/**
 * Numeric weights aligned with Bootstrap’s `fw-*` scale (`fw-normal` → 400, etc.).
 * Use in `style` objects (react-select, portals, third-party components) where `className` / `fw-*` do not apply.
 */
export const FONT_WEIGHT = {
  normal: 400,
  medium: 500,
  semibold: 600,
}

/**
 * Canonical UI surface & text tokens — defined as `--aug-ui-*` in `_augmentt-theme-colors-light.scss`
 * and `_augmentt-theme-colors-dark.scss`. Use in inline styles (react-select, datepicker, etc.) so
 * page background, hover/highlight, and type track one set of variables.
 */
export const AUG_UI = {
  bg: 'var(--aug-ui-bg)',
  surface: 'var(--aug-ui-surface)',
  hover: 'var(--aug-ui-hover)',
  elevated: 'var(--aug-ui-elevated)',
  fg: 'var(--aug-ui-fg)',
  fgMuted: 'var(--aug-ui-fg-muted)',
  border: 'var(--aug-ui-border)',
  accent: 'var(--aug-ui-accent)',
  onAccent: 'var(--aug-ui-on-accent)',
}

/*  selectStyle: Applies a custom style for a custom react-select control
 *  returns:
 *    <object> : Custom style object
 */
export const selectStyle = {
  option: (base, state) => ({
    ...base,
    color: state.value === '-' ? AUG_UI.accent : AUG_UI.fg,
    backgroundColor: AUG_UI.bg,
    ':active': {
      color: AUG_UI.fg,
      backgroundColor: AUG_UI.hover,
    },
    ':hover': {
      color: AUG_UI.accent,
      backgroundColor: AUG_UI.hover,
    },
    padding: '0.5rem 1.5rem',
    fontSize: '1rem',
    fontWeight: state.isSelected ? FONT_WEIGHT.semibold : FONT_WEIGHT.medium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  control: (base) => ({
    ...base,
    '&:hover': { borderColor: 'var(--aug-ui-divider-subtle)' },
    boxShadow: 'none',
    borderColor: 'var(--aug-ui-divider-subtle)',
    backgroundColor: AUG_UI.bg,
    color: AUG_UI.fg,
    fontWeight: FONT_WEIGHT.semibold,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '1rem',
    color: AUG_UI.fg,
  }),
  placeholder: (base) => ({
    ...base,
    color: AUG_UI.fgMuted,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
  }),
  clearIndicator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
    color: AUG_UI.accent,
    fontWeight: FONT_WEIGHT.normal,
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    width: 30,
    color: state.isFocused ? AUG_UI.fg : AUG_UI.fgMuted,
  }),
  menu: (base) => ({
    ...base,
    boxShadow: '0 4px 8px 0 rgba(52, 58, 64, 0.15)',
    border: 'solid 1px var(--aug-ui-divider-subtle)',
    backgroundColor: AUG_UI.bg,
    borderRadius: '4px',
    overflow: 'hidden',
    minWidth: 165,
    maxHeight: 240,
    width: '100%',
    zIndex: 1000,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: AUG_UI.bg,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: AUG_UI.hover,
    borderRadius: 10,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: AUG_UI.accent,
    fontSize: '1rem',
    fontWeight: FONT_WEIGHT.medium,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: AUG_UI.accent,
    fontSize: '1rem',
    fontWeight: FONT_WEIGHT.medium,
    ':hover': {
      color: AUG_UI.accent,
      backgroundColor: AUG_UI.hover,
      borderRadius: 10,
    },
  }),
}

/**
 * Merges react-select styles with menuPortal z-index above modal.
 * Use for Select components rendered inside a modal so the menu appears on top.
 */
export const selectStyleMenuAboveModal = (baseStyles = selectStyle) => ({
  ...baseStyles,
  menuPortal: (base) => ({
    ...(typeof baseStyles.menuPortal === 'function' ? baseStyles.menuPortal(base) : base),
    zIndex: Z_INDEX.ABOVE_MODAL,
  }),
})

/*  multiSelectStyle: Applies a custom style for a multi-select react-select control
 *  returns:
 *    <object> : Custom style object
 */
export const multiSelectStyle = {
  option: (base, state) => ({
    ...base,
    color: state.value === '-' ? AUG_UI.accent : AUG_UI.fg,
    backgroundColor: AUG_UI.bg,
    ':active': {
      color: AUG_UI.fg,
      backgroundColor: AUG_UI.hover,
    },
    ':hover': {
      color: AUG_UI.accent,
      backgroundColor: AUG_UI.hover,
    },
    padding: '0.5rem 1rem',
    fontWeight: FONT_WEIGHT.medium,
    textOverflow: 'ellipsis',
  }),
  control: (base) => ({
    ...base,
    '&:hover': { borderColor: 'var(--aug-ui-divider-subtle)' },
    boxShadow: 'none',
    borderColor: 'var(--aug-ui-divider-subtle)',
    backgroundColor: AUG_UI.bg,
    color: AUG_UI.fg,
    fontWeight: FONT_WEIGHT.semibold,
  }),
  singleValue: (base) => ({
    ...base,
    color: AUG_UI.fg,
  }),
  placeholder: (base) => ({
    ...base,
    color: AUG_UI.fgMuted,
    position: 'relative',
    lineHeight: '2.5rem',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
  }),
  clearIndicator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
    color: AUG_UI.accent,
    fontWeight: FONT_WEIGHT.normal,
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    width: 30,
    color: state.isFocused ? AUG_UI.fg : AUG_UI.fgMuted,
  }),
  menu: (base) => ({
    ...base,
    boxShadow: '0 4px 8px 0 rgba(52, 58, 64, 0.15)',
    border: 'solid 1px var(--aug-ui-divider-subtle)',
    backgroundColor: AUG_UI.bg,
    borderRadius: '4px',
    overflow: 'hidden',
    minWidth: 165,
    maxHeight: 240,
    width: 'auto',
    zIndex: 100,
  }),
  menuList: (base) => ({
    ...base,
    padding: '0px',
    minWidth: 235,
    backgroundColor: AUG_UI.bg,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: AUG_UI.hover,
    borderRadius: 10,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: AUG_UI.accent,
    fontSize: '1rem',
    fontWeight: FONT_WEIGHT.medium,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: AUG_UI.accent,
    fontSize: '1rem',
    fontWeight: FONT_WEIGHT.medium,
    ':hover': {
      color: AUG_UI.accent,
      backgroundColor: AUG_UI.hover,
      borderRadius: 10,
    },
  }),
  input: () => ({
    position: 'absolute',
    color: AUG_UI.fg,
  }),
  valueContainer: (base, state) => ({
    ...base,
    color: AUG_UI.fg,
    width: state.isSelected ? '35%' : '70%',
    padding: '0 8px',
    flexWrap: 'nowrap',
    height: '30px',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    justifyContent: 'space-between',
  }),
}

/*  selectStyleDot: Applies a custom style for a custom react-select control with a colored dot (representing status)
 *  returns:
 *    <object> : Custom style object
 */
export const selectStyleDot = {
  option: (base) => ({
    ...base,
    color: AUG_UI.fg,
    backgroundColor: AUG_UI.bg,
    ':active': {
      color: AUG_UI.fg,
      backgroundColor: AUG_UI.hover,
    },
    ':hover': {
      color: AUG_UI.accent,
      backgroundColor: AUG_UI.hover,
    },
    padding: '0.5rem 1rem',
    fontWeight: FONT_WEIGHT.medium,
  }),
  control: (base) => ({
    ...base,
    '&:hover': { borderColor: 'var(--aug-ui-divider-subtle)' },
    boxShadow: 'none',
    borderColor: 'var(--aug-ui-divider-subtle)',
    backgroundColor: AUG_UI.bg,
    fontWeight: FONT_WEIGHT.semibold,
  }),
  // eslint-disable-next-line no-use-before-define
  input: (base) => ({ ...base, ...dot() }),
  // eslint-disable-next-line no-use-before-define
  placeholder: (base) => ({ ...base, ...dot() }),
  singleValue: (base, { data }) => ({
    ...base,
    /* eslint-disable no-nested-ternary */
    // eslint-disable-next-line no-use-before-define
    ...dot(
      data.value === 0
        ? AUG_UI.hover
        : (data.value === 1 && (data.type === 'status' || data.type === 'productivity')) ||
            (data.value === 3 && data.type === 'risk')
          ? 'var(--bs-danger)'
          : data.value === 2
            ? 'var(--bs-warning)'
            : (data.value === 3 && (data.type === 'status' || data.type === 'productivity')) ||
                (data.value === 1 && data.type === 'risk')
              ? 'var(--bs-success)'
              : undefined,
    ),
    /* eslint-enable no-nested-ternary */
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
  }),
  menu: (base) => ({
    ...base,
    boxShadow: '0 4px 8px 0 rgba(52, 58, 64, 0.15)',
    border: 'solid 1px var(--aug-ui-divider-subtle)',
    minWidth: 165,
    maxHeight: 240,
    width: '100%',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

/** selectStyleLarge: Applies a custom style for a custom react-select control. (Used in main company/employee select controls).
 *  returns:
 *    <object> : Custom style object
 */
export const selectStyleLarge = {
  option: (base, state) => ({
    ...base,
    // eslint-disable-next-line no-nested-ternary
    color: state.isSelected ? AUG_UI.onAccent : state.isFocused ? AUG_UI.accent : AUG_UI.fg,
    // eslint-disable-next-line no-nested-ternary
    backgroundColor: state.isSelected ? AUG_UI.accent : state.isFocused ? AUG_UI.hover : null,
    ':active': {
      color: AUG_UI.fg,
      backgroundColor: AUG_UI.hover,
    },
    padding: '0.5rem 1.5rem',
    cursor: 'pointer',
  }),
  container: (base, state) => ({
    ...base,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto',
  }),
  control: (base, state) => ({
    ...base,
    '&:hover': { borderColor: 'transparent' },
    border: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    color: state.isDisabled ? AUG_UI.fgMuted : AUG_UI.fg,
    fontWeight: FONT_WEIGHT.semibold,
    fontSize: '1.4rem',
    opacity: state.isDisabled ? 0.65 : 1,
  }),
  valueContainer: (base, state) => ({
    ...base,
    width: state.hasValue ? 'fit-content' : 150,
    maxWidth: 250,
    padding: '0',
    color: state.isDisabled ? AUG_UI.fgMuted : AUG_UI.fg,
  }),
  singleValue: (base, state) => ({
    ...base,
    display: 'contents',
    color: state.isDisabled ? AUG_UI.fgMuted : AUG_UI.fg,
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isDisabled ? AUG_UI.fgMuted : AUG_UI.fg,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
  }),
  menu: (base) => ({
    ...base,
    boxShadow: '0px 0px 15px -5px rgba(82, 63, 105, 0.15)',
    backgroundColor: AUG_UI.bg,
    border: '1px solid var(--aug-ui-divider-subtle)',
    width: 165,
    visibility: 'visible',
    zIndex: 3,
  }),
  menuList: (base) => ({ ...base, overflow: 'hidden' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

/*  datePickerStyle: Applies a custom style for the datepicker component which uses react-select
 *  returns:
 *    <object> : Custom style object
 */
export const datePickerStyle = {
  option: (base, state) => ({
    ...base,
    color: state.isSelected ? AUG_UI.onAccent : AUG_UI.fg,
    // eslint-disable-next-line no-nested-ternary
    backgroundColor: state.isSelected ? AUG_UI.accent : state.isFocused ? AUG_UI.hover : AUG_UI.bg,
    ':active': {
      color: AUG_UI.fg,
      backgroundColor: AUG_UI.hover,
    },
  }),
  control: (base) => ({
    ...base,
    '&:hover': { borderColor: 'var(--aug-ui-divider-subtle)' },
    width: '325px',
    backgroundColor: AUG_UI.bg,
    borderColor: 'var(--aug-ui-divider-subtle)',
    color: AUG_UI.fg,
  }),
  singleValue: (base) => ({
    ...base,
    // eslint-disable-next-line no-use-before-define
    ...dateIcon(),
    fontWeight: FONT_WEIGHT.semibold,
    color: AUG_UI.fg,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
  }),
  // eslint-disable-next-line no-use-before-define
  input: (styles) => ({ ...styles, ...dateIcon() }),
  // eslint-disable-next-line no-use-before-define
  placeholder: (styles) => ({ ...styles, ...dateIcon() }),
}

export const datePickerStyleReporting = {
  option: (base, state) => ({
    ...base,
    color: state.isSelected ? AUG_UI.onAccent : AUG_UI.fg,
    // eslint-disable-next-line no-nested-ternary
    backgroundColor: state.isSelected ? AUG_UI.accent : state.isFocused ? AUG_UI.hover : null,
    ':active': {
      color: AUG_UI.fg,
      backgroundColor: AUG_UI.hover,
    },
  }),
  control: (base) => ({
    ...base,
    '&:hover': { borderColor: 'var(--aug-ui-divider-subtle)' },
    width: 'auto',
    backgroundColor: AUG_UI.bg,
    borderColor: 'var(--aug-ui-divider-subtle)',
  }),
  singleValue: (base) => ({
    ...base,
    // eslint-disable-next-line no-use-before-define
    ...dateIcon(),
    fontWeight: FONT_WEIGHT.semibold,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'var(--aug-ui-divider-subtle)',
  }),
  // eslint-disable-next-line no-use-before-define
  input: (styles) => ({ ...styles, ...dateIcon() }),
  // eslint-disable-next-line no-use-before-define
  placeholder: (styles) => ({ ...styles, ...dateIcon() }),
}

/*  selectLicenseStyle: Applies a custom style for a custom react-select control
 *  returns:
 *    <object> : Custom style object
 */
export const selectLicenseStyle = {
  option: (base, state) => ({
    ...base,
    // eslint-disable-next-line no-use-before-define
    ...featureIcon(state.value),
    color: state.value === '-' ? AUG_UI.accent : AUG_UI.fg,
    backgroundColor: AUG_UI.bg,
    ':active': {
      color: AUG_UI.fg,
      backgroundColor: AUG_UI.hover,
    },
    ':hover': {
      color: AUG_UI.accent,
      backgroundColor: AUG_UI.hover,
    },
    padding: '0.5rem',
    fontSize: '1rem',
    fontWeight: state.isSelected ? FONT_WEIGHT.semibold : FONT_WEIGHT.medium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  control: (base) => ({
    ...base,
    '&:hover': { borderColor: 'var(--aug-ui-divider-subtle)' },
    boxShadow: 'none',
    borderColor: 'var(--aug-ui-divider-subtle)',
    backgroundColor: AUG_UI.bg,
    color: AUG_UI.fg,
    fontWeight: FONT_WEIGHT.semibold,
  }),
  placeholder: (base) => ({
    ...base,
    color: AUG_UI.fgMuted,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
  }),
  clearIndicator: (base) => ({
    ...base,
    backgroundColor: 'transparent',
    color: AUG_UI.accent,
    fontWeight: FONT_WEIGHT.normal,
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    width: 30,
    color: state.isFocused ? AUG_UI.fg : AUG_UI.fgMuted,
  }),
  menu: (base) => ({
    ...base,
    boxShadow: '0 4px 8px 0 rgba(52, 58, 64, 0.15)',
    border: 'solid 1px var(--aug-ui-divider-subtle)',
    backgroundColor: AUG_UI.bg,
    minWidth: 165,
    maxHeight: 240,
    width: '100%',
    zIndex: 1000,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: AUG_UI.bg,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: AUG_UI.hover,
    borderRadius: 10,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: AUG_UI.accent,
    fontSize: '1rem',
    fontWeight: FONT_WEIGHT.medium,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: AUG_UI.accent,
    fontSize: '1rem',
    fontWeight: FONT_WEIGHT.medium,
    ':hover': {
      color: AUG_UI.accent,
      backgroundColor: AUG_UI.hover,
      borderRadius: 10,
    },
  }),
  // eslint-disable-next-line no-use-before-define
  input: (base) => ({ ...base, ...featureIcon(), color: AUG_UI.fg }),
  singleValue: (base, { data }) => ({
    ...base,
    // eslint-disable-next-line no-use-before-define
    ...featureIcon(data.value),
    color: AUG_UI.fg,
  }),
}

/*  dot: Returns the style info for a colored dot
 *  returns:
 *    <object> : Custom style object
 */
const dot = (color = AUG_UI.fgMuted) => ({
  alignItems: 'center',
  display: 'flex',
  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10,
  },
})

/*  dateIcon: Returns the style info for a date/calendar icon
 *  returns:
 *    <object> : Custom style object
 */
const dateIcon = () => ({
  alignItems: 'center',
  display: 'flex',
  ':before': {
    font: 'var(--fa-font-regular)',
    fontVariant: 'normal',
    content: '"073"',
    color: AUG_UI.accent,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 24,
    width: 24,
    lineHeight: '24px',
    flexShrink: 0,
    fontSize: '1.2rem',
    marginRight: '1rem',
  },
})

const featureIcon = (value = undefined) => ({
  alignItems: 'center',
  display: 'flex',
  ':before': {
    content: '""',
    backgroundImage: `url(${
      // eslint-disable-next-line no-nested-ternary
      value === 0 ? secureEssentialsIcon : value === 1 ? securePremiumIcon : undefined
    })`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: '65%',
    display: 'block',
    height: 24,
    width: 24,
    marginRight: '0.25rem',
  },
})
